import { useEffect, useState } from "react";
import dayjs from "dayjs";

const CLOCK_INTERVAL_MS = 60 * 1000;
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

function validDate(value) {
    if (value === undefined || value === null || value === "") return null;

    const parsed = dayjs(value);
    return parsed.isValid() ? parsed : null;
}

export function getNotificationVisibleDate(notification) {
    const createdDate = validDate(notification?.date);
    const embargoDate = validDate(notification?.fromDate);

    if (embargoDate && (!createdDate || embargoDate.isAfter(createdDate))) {
        return embargoDate;
    }

    return createdDate || embargoDate;
}

export function getNotificationVisibleTimestamp(notification) {
    return getNotificationVisibleDate(notification)?.valueOf() || 0;
}

export function isNotificationVisible(notification, now = Date.now()) {
    const visibleDate = getNotificationVisibleDate(notification);
    if (!visibleDate) return true;

    return !visibleDate.isAfter(dayjs(now));
}

export function compareNotificationsByVisibleDate(a, b) {
    return (
        getNotificationVisibleTimestamp(b) -
        getNotificationVisibleTimestamp(a)
    );
}

// Nodiant Coder (desktop app) cards belong in the popup of NEW notifications
// only: the home feed leaves them out (people reach their key from Desktop App
// in the menu). In the popup only the newest shows, as they all open the same
// page, and only while the person has a valid key.
const DESKTOP_APP_TYPE = "desktopApp";

export function withoutDesktopAppNotifications(notifications) {
    return notifications.filter(
        (notification) => notification.type !== DESKTOP_APP_TYPE,
    );
}

export function withLatestDesktopAppNotificationOnly(
    notifications,
    now = Date.now(),
    hasDesktopLicence = true,
) {
    if (!hasDesktopLicence) {
        return withoutDesktopAppNotifications(notifications);
    }

    let latest = null;

    notifications.forEach((notification) => {
        if (
            notification.type === DESKTOP_APP_TYPE &&
            isNotificationVisible(notification, now) &&
            (!latest ||
                getNotificationVisibleTimestamp(notification) >
                    getNotificationVisibleTimestamp(latest))
        ) {
            latest = notification;
        }
    });

    return notifications.filter(
        (notification) =>
            notification.type !== DESKTOP_APP_TYPE || notification === latest,
    );
}

// Counts what the popup would show
export function countUnreadVisibleNotifications(
    notifications,
    userKey,
    now = Date.now(),
    hasDesktopLicence = true,
) {
    if (!userKey) return 0;

    return withLatestDesktopAppNotificationOnly(
        notifications,
        now,
        hasDesktopLicence,
    ).filter(
        (notification) =>
            isNotificationVisible(notification, now) &&
            !notification.seenBy?.includes(userKey),
    ).length;
}

export function useNotificationClock(notifications) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const tick = () => setNow(Date.now());
        const intervalId = window.setInterval(tick, CLOCK_INTERVAL_MS);
        const currentTime = Date.now();
        const nextReleaseTime = notifications
            .map(getNotificationVisibleTimestamp)
            .filter((timestamp) => timestamp > currentTime)
            .sort((a, b) => a - b)[0];
        const timeoutId = nextReleaseTime
            ? window.setTimeout(
                  tick,
                  Math.min(
                      Math.max(nextReleaseTime - currentTime + 25, 25),
                      MAX_TIMEOUT_MS,
                  ),
              )
            : null;

        return () => {
            window.clearInterval(intervalId);
            if (timeoutId !== null) window.clearTimeout(timeoutId);
        };
    }, [notifications, now]);

    return now;
}
